import { NIGHTMARES } from '@somnhold/content';
import { cellAtWorld, cellCenter, findPath, heroWalkable } from '../grid.ts';
import { dist2, iatan2, imax, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, SendaNpc, SendaRules } from '../types.ts';
import { ev } from './combat.ts';
import { stepToward } from './enemies.ts';
import { wedgeGoal, wedgeResult, wedgeTick } from './wedges.ts';
import { hookOrder, hooksGoal, hooksResult, hooksTick } from './hooks.ts';
import { gustGoal, gustOrder, gustResult, gustTick } from './gusts.ts';
import { weightGoal, weightOrder, weightResult, weightTick } from './weights.ts';
import { slopeGoal, slopeResult, slopeTick } from './slope.ts';
import { roofGoal, roofOrder, roofResult, roofTick } from './roof.ts';
import { slideGoal, slideOrder, slideResult, slideTick } from './slide.ts';
import { caveGoal, caveResult, caveTick } from './cave.ts';
import { cragGoal, cragResult, cragTick } from './crag.ts';
import { lineGoal, lineOrder, lineResult, lineTick } from './line.ts';
import { beamGoal, beamOrder, beamResult, beamTick } from './beam.ts';
import { stormGoal, stormOrder, stormResult, stormTick } from './storm.ts';
import { canalGoal, canalOrder, canalResult, canalTick, canalWet } from './canals.ts';
import { kindleTick } from './kindle.ts';
import { saltGoal, saltOrder, saltResult, saltTick } from './salt.ts';
import { archCover, archGoal, archOrder, archResult, archTick } from './arch.ts';
import { monasteryGoal, monasteryResult, monasteryTick, monOrder } from './monastery.ts';
import { seventhGoal, seventhResult, seventhTick, sevOrder, sevOwnBlow } from './seventh.ts';
import { corniceGoal, corniceResult, corniceTick } from './cornice.ts';
import { workshopGoal, workshopResult, workshopTick } from './workshop.ts';
import { gearOrder, gearsGoal, gearsResult, gearsTick } from './gears.ts';
import { golemOf } from './golems.ts';
import { craneGoal, craneResult, craneTick } from './crane.ts';
import { gondolaGoal, gondolaResult, gondolaTick } from './gondola.ts';
import { boxOrder, gearboxGoal, gearboxResult, gearboxTick } from './gearbox.ts';
import { gargOf } from './garg.ts';
import { ventGoal, ventResult, ventTick } from './hondo.ts';
import { emberGoal, emberResult, emberTick } from './brasa.ts';
import { camOrder, camsGoal, camsResult, camsTick } from './cams.ts';
import { tenazaOf } from './tenaza.ts';
import { strutOrder, strutsGoal, strutsResult, strutsTick } from './struts.ts';
import { cartOrder, cartsGoal, cartsResult, cartsTick, cartBeetle } from './carts.ts';
import { drillOrder, drillsGoal, drillsResult, drillsTick } from './drills.ts';
import { barrenaOf } from './barrena.ts';
import { kneeOrder, kneesGoal, kneesResult, kneesTick } from './knees.ts';
import { tornoGoal, tornoOrder, tornoResult, tornoTick } from './torno.ts';
import { shellGoal, shellOrder, shellResult, shellTick } from './shell.ts';
import { platesGoal, platesOwn, platesResult, platesTick } from './plates.ts';
import { pensGoal, pensResult, pensTick } from './pens.ts';
import { trailGoal, trailOrder, trailResult, trailTick } from './trail.ts';
import { vertGoal, vertMarkOff, vertOrder, vertResult, vertTick } from './vertebras.ts';
import { boteGoal, boteOrder, boteResult, boteTick } from './bote.ts';
import { islaGoal, islaMarkOff, islaOrder, islaResult, islaTick } from './isla.ts';
import { rocaGoal, rocaMarkOff, rocaOrder, rocaResult, rocaTick } from './roca.ts';
import { wolfOwnBlow } from './graywolf.ts';
import { litterGoal, litterOrder, litterResult, litterTick } from './litter.ts';
import { druidGoal, druidMarkOff, druidOrder, druidResult, druidTick, gladeBlow } from './druid.ts';
import { summerGoal, summerMarkOff, summerOrder, summerResult, summerTick } from './druid-summer.ts';
import { autumnGoal, autumnSpares, autumnMarkOff, autumnOrder, autumnResult, autumnTick, autumnWalks } from './druid-autumn.ts';
import { winterGoal, winterMarkOff, winterOrder, winterResult, winterSpares, winterTick } from './druid-winter.ts';
import { deepGoal, deepMarkOff, deepOrder, deepResult, deepSpares, deepTick } from './druid-roots.ts';
import { cairnOrder, cairnsGoal, cairnsResult, cairnsTick } from './cairns.ts';
import { echoGoal, echoOrder, echoResult, echoTick } from './echo.ts';
import { fallenGoal, fallenOrder, fallenOwn, fallenResult, fallenTick } from './fallen.ts';
import { cliffGoal, cliffHolds, cliffOff, cliffOrder, cliffOwn, cliffResult, cliffTick } from './cliff.ts';
import { sealOrder, sealsGoal, sealsResult, sealsTick, sootGoal, sootResult, sootTick } from './biblio.ts';

/**
 * A path's fight (docs/SENDAS.md; rules in `rules.senda`, built by campaign/sendas.ts): the base every act stands on.
 * Its people (life in blows: every so often each nightmare close by lands one) follow the hero, work at their spots or
 * wait; its lines are said by the sim at fixed moments, so every client and the server hear the same ones; its goal
 * (lasting, a boss, the people's work, the field cleared, the ledge's column) and its half, for the lines and the
 * enemy's scars. A fight without `rules.senda` never comes here. Nothing walks every nightmare against every other:
 * each person looks at the nightmares once a second (docs/RENDIMIENTO.md, «Reglas para lo nuevo»).
 */

type St = NonNullable<BattleState['senda']>;
type Say = SendaRules['says'][number];
type NpcIn = SendaRules['npcs'][number];

const fresh = (r: SendaRules): St => ({
  npcs: r.npcs.map(
    (n): SendaNpc => ({ id: n.id, x: n.x, y: n.y, hp: n.hp, maxHp: n.hp, facing: 0, moving: false, spot: 0, held: 0, flinch: 0, hit: false }),
  ),
  work: 0,
  said: [],
  half: false,
  boss: false,
  nums: {},
});

/** Says one line now: over the person who says it if on the field, else from the hero's place (a voice off the field). */
const speak = (s: BattleState, st: St, l: Say): void => {
  const by = st.npcs.find((n) => n.id === l.who);
  const h = by ? null : s.heroes[0];
  ev(s, { t: 'say', who: l.who, line: l.line, x: by ? by.x : (h?.x ?? 0), y: by ? by.y : (h?.y ?? 0) });
};

/** Says (or queues, with its delay) every line of this kind not yet said (`npc`: only about that person; `cue`: that cue's). */
const say = (s: BattleState, r: SendaRules, st: St, on: Say['on'], npc = '', cue = ''): void => {
  for (let i = 0; i < r.says.length; i++) {
    const l = r.says[i] as Say;
    if (l.on !== on || st.said.includes(i) || (on === 'npcHit' && l.npc !== npc) || (on === 'at' && s.waveT < l.t) || (on === 'cue' && l.cue !== cue)) continue;
    st.said.push(i);
    if (l.delay) (st.queued ??= []).push({ i, at: s.tick + l.delay });
    else speak(s, st, l);
  }
};

/** A moment of the fight's own (a shield dug, planted, the cornice down): its lines, once. */
export const sendaCue = (s: BattleState, cue: string): void => {
  const r = s.rules?.senda;
  const st = s.senda;
  if (!r || !st) return;
  const cues = (st.cues ??= []);
  if (!cues.includes(cue)) cues.push(cue);
  say(s, r, st, 'cue', '', cue);
};

/** A tap on one of the cut bridge's marks (the `mark` command): a rope, a cover's cell, a piece to a pan. */
export const sendaMark = (s: BattleState, h: Hero, at: number, to: number): void => {
  const r = s.rules?.senda;
  if (!r) return;
  if (r.hooks) hookOrder(s, h, at);
  else if (r.gust) gustOrder(s, h, at);
  else if (r.weight) weightOrder(s, h, at, to);
  else if (r.roof) roofOrder(s, h, at, to);
  else if (r.slide?.lanes) slideOrder(s, h, at);
  else if (r.line) lineOrder(s, h, at);
  else if (r.beam) beamOrder(s, h, at);
  else if (r.storm) stormOrder(s, h, at);
  else if (r.canal) canalOrder(s, h, at);
  else if (r.salt) saltOrder(s, h, at);
  else if (r.arch) archOrder(s, h, at);
  else if (r.monastery) monOrder(s, h, at);
  else if (r.seventh) sevOrder(s, h, at);
  else if (r.gears) gearOrder(s, h, at);
  else if (r.gearbox) boxOrder(s, h, at);
  else if (r.cams) camOrder(s, h, at);
  else if (r.struts) strutOrder(s, h, at);
  else if (r.carts) cartOrder(s, h, at);
  else if (r.drills) drillOrder(s, h, at);
  else if (r.knees) kneeOrder(s, h, at);
  else if (r.torno) tornoOrder(s, h, at);
  else if (r.shell) shellOrder(s, h, at);
  else if (r.trail) trailOrder(s, h, at);
  else if (r.vert) vertOrder(s, h, at);
  else if (r.bote) boteOrder(s, h, at);
  else if (r.isla) islaOrder(s, h, at);
  else if (r.roca) rocaOrder(s, h, at, (c) => sendaCue(s, c));
  else if (r.litter) litterOrder(s, h, at);
  else if (r.druid) druidOrder(s, h, at);
  else if (r.summer) summerOrder(s, h, at);
  else if (r.autumn) autumnOrder(s, h, at);
  else if (r.winter) winterOrder(s, h, at);
  else if (r.deep) deepOrder(s, h, at);
  else if (r.cairns) cairnOrder(s, h, at);
  else if (r.echo) echoOrder(s, h, at, (c) => sendaCue(s, c));
  else if (r.fallen) fallenOrder(s, h, at);
  else if (r.cliff) cliffOrder(s, h, at);
  else if (r.seals) sealOrder(s, h, at);
};

/** Another order of the hero's own: what it was sent to do there is off (a piece in its hands stays in them). */
export const sendaMarkOff = (s: BattleState): void => {
  const st = s.senda;
  if (st?.hooks) st.hooks.at = -1;
  if (st?.gust) st.gust.job = -1;
  if (st?.weight) st.weight.job = -1;
  if (st?.roof) st.roof.job = -1;
  if (st?.slide) st.slide.at = -1;
  if (st?.line) st.line.at = -1;
  if (st?.beam) st.beam.at = -1;
  if (st?.storm) st.storm.at = -1;
  if (st?.canal) st.canal.job = -1;
  if (st?.salt) st.salt.job = -1;
  if (st?.arch) st.arch.job = -1;
  if (st?.monastery) st.monastery.job = -1;
  if (st?.seventh) st.seventh.job = -1;
  if (st?.gears) st.gears.job = -1;
  if (st?.gearbox) st.gearbox.job = -1;
  if (st?.cams) st.cams.job = -1;
  if (st?.carts) st.carts.job = -1;
  if (st?.drills) st.drills.job = -1;
  if (st?.knees) st.knees.job = -1;
  if (st?.torno) st.torno.job = -1;
  if (st?.shell) st.shell.job = -1;
  if (st?.trail) st.trail.job = -1;
  if (st?.vert) vertMarkOff(s);
  if (st?.isla) islaMarkOff(s);
  if (st?.roca) rocaMarkOff(s);
  if (st?.litter) st.litter.job = -1;
  if (st?.druid) druidMarkOff(s);
  if (st?.summer) summerMarkOff(s);
  if (st?.autumn) autumnMarkOff(s);
  if (st?.winter) winterMarkOff(s);
  if (st?.deep) deepMarkOff(s);
  if (st?.cairns) st.cairns.job = -1;
  if (st?.echo) st.echo.job = -1;
  if (st?.fallen) st.fallen.job = -1;
  if (st?.cliff) cliffOff(s);
  if (st?.seals) st.seals.job = -1;
};

/** The boss of the fight: the field's, or the descent's last hall's. */
const bossOf = (s: BattleState): number => s.bossId ?? s.dungeon?.boss ?? 0;

/** How far the goal has come, out of `need` (the boss: its life taken). */
export const sendaGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda;
  const st = s.senda;
  if (!r || !st) return { done: 0, need: 1 };
  const g = r.goal;
  if (g.kind === 'time') return { done: s.waveT, need: g.need };
  if (g.kind === 'work') return { done: st.work, need: g.need };
  if (g.kind === 'wedge') return wedgeGoal(s);
  if (g.kind === 'winch') return hooksGoal(s);
  if (g.kind === 'cross') return gustGoal(s);
  if (g.kind === 'weight') return weightGoal(s);
  if (g.kind === 'door') return slopeGoal(s);
  if (g.kind === 'troll') return roofGoal(s);
  if (g.kind === 'mouth' || g.kind === 'avalanche') return slideGoal(s);
  if (g.kind === 'hall') return caveGoal(s);
  if (g.kind === 'crag') return cragGoal(s);
  if (g.kind === 'line') return lineGoal(s);
  if (g.kind === 'beam') return beamGoal(s);
  if (g.kind === 'storm') return stormGoal(s);
  if (g.kind === 'pool' || g.kind === 'ebb') return canalGoal(s);
  if (g.kind === 'house' || g.kind === 'drain') return saltGoal(s);
  if (g.kind === 'ends' || g.kind === 'cistern' || g.kind === 'mammoth') return archGoal(s);
  if (g.kind === 'tide' || g.kind === 'cell' || g.kind === 'sixth') return monasteryGoal(s);
  if (g.kind === 'rubble' || g.kind === 'ring' || g.kind === 'fled') return seventhGoal(s);
  if (g.kind === 'grillo') return corniceGoal(s);
  if (g.kind === 'tormo') return workshopGoal(s);
  if (g.kind === 'lift') return gearsGoal(s);
  if (g.kind === 'crane') return craneGoal(s);
  if (g.kind === 'span') return gondolaGoal(s);
  if (g.kind === 'haul') return gearboxGoal(s);
  if (g.kind === 'marrow') return ventGoal(s);
  if (g.kind === 'unjam') return emberGoal(s);
  if (g.kind === 'stone') return camsGoal(s);
  if (g.kind === 'struts') return strutsGoal(s);
  if (g.kind === 'drillHall') return cartsGoal(s);
  if (g.kind === 'geode') return drillsGoal(s);
  if (g.kind === 'knees') return kneesGoal(s);
  if (g.kind === 'raja') return tornoGoal(s);
  if (g.kind === 'shell') return shellGoal(s);
  if (g.kind === 'free') return platesGoal(s);
  if (g.kind === 'herd') return pensGoal(s);
  if (g.kind === 'trail') return trailGoal(s);
  if (g.kind === 'way' || g.kind === 'leaps' || g.kind === 'boat') return vertGoal(s);
  if (g.kind === 'wake' || g.kind === 'waves' || g.kind === 'elder') return boteGoal(s);
  if (g.kind === 'floes' || g.kind === 'lagoon' || g.kind === 'seal') return islaGoal(s);
  if (g.kind === 'climb' || g.kind === 'mouths' || g.kind === 'bed' || g.kind === 'wolf') return rocaGoal(s);
  if (g.kind === 'litter' || g.kind === 'lamp' || g.kind === 'night' || g.kind === 'dawn' || g.kind === 'mirrors' || g.kind === 'lamps' || g.kind === 'procession' || g.kind === 'mourn') return litterGoal(s);
  if (g.kind === 'glade' || g.kind === 'face' || g.kind === 'bridge') return druidGoal(s);
  if (g.kind === 'kilns' || g.kind === 'strip' || g.kind === 'cone') return summerGoal(s);
  if (g.kind === 'maple' || g.kind === 'houses' || g.kind === 'rot') return autumnGoal(s);
  if (g.kind === 'crown' || g.kind === 'bandage' || g.kind === 'sap') return winterGoal(s);
  if (g.kind === 'deepMouths' || g.kind === 'carcoma') return deepGoal(s);
  if (g.kind === 'net') return autumnGoal(s);
  if (g.kind === 'cairns') return cairnsGoal(s);
  if (g.kind === 'echo') return echoGoal(s);
  if (g.kind === 'fallen') return fallenGoal(s);
  if (g.kind === 'cliffEdge' || g.kind === 'cliffWall' || g.kind === 'cliffNight') return cliffGoal(s);
  if (g.kind === 'catalog') return sootGoal(s);
  if (g.kind === 'locks' || g.kind === 'cabinet') return sealsGoal(s);
  // A descent to its last hall (the paladin's chapter 5, the cleric's 4): the halls cleared on the way, counted as the
  // crypt's own plate counts them («Sala n de N»: neither the entrance nor the last hall, which is where it is won).
  if (g.kind === 'reach') {
    const d = s.dungeon;
    const n = d ? imax(1, d.cleared.length - 2) : 1;
    return { done: d?.reached ? n : d ? d.cleared.slice(1, d.cleared.length - 1).filter(Boolean).length : 0, need: n };
  }
  if (g.kind === 'boss') {
    const id = bossOf(s);
    const b = id ? s.enemies.find((e) => e.id === id) : undefined;
    if (!b) return { done: st.boss ? 1 : 0, need: 1 };
    return { done: b.maxHp - (b.hp > 0 ? b.hp : 0), need: b.maxHp };
  }
  const total = s.wave.spawns.length;
  return { done: s.stats.kills, need: total > 0 ? total : 1 };
};

/** Walks a person toward a point: straight across an open field, along a found way where walls stand (a descent). */
const walk = (s: BattleState, n: SendaNpc, x: number, y: number, speed: number): void => {
  // (The workshop's halls have walls between them: straight on while the next step is floor, else round them.)
  // (And the mine's gallery winds between its walls: the artificer's chapter 4.)
  // (And the Furrow in the fog: the ranger's chapter 1, act 2; and the wall's ledges, chapter 2.)
  if (s.mode !== 'dungeon' && !((s.rules?.senda?.workshop || s.rules?.senda?.carts || s.rules?.senda?.echo || s.rules?.senda?.cliff) && !clearAhead(s, n, x, y))) {
    stepToward(n, x, y, speed);
    return;
  }
  const from = cellAtWorld(s, n.x, n.y);
  const to = cellAtWorld(s, x, y);
  if (from === to || from < 0 || to < 0) {
    stepToward(n, x, y, speed);
    return;
  }
  const way = findPath(s, from, to);
  const next = way[0];
  // A door shut between (a descent shuts them behind the party, for good): the person comes in with the hero, as a
  // hero left in the corridor is pulled in.
  if (next === undefined || way[way.length - 1] !== to) {
    if (!withinRange(n.x, n.y, x, y, 2500)) {
      n.x = x + 300;
      n.y = y + 300;
    }
    return;
  }
  const c = cellCenter(s.map.radius, next);
  stepToward(n, c.x, c.y, speed);
};

/** Whether the next step from a person toward a point is floor (a hex on). */
const clearAhead = (s: BattleState, n: SendaNpc, x: number, y: number): boolean => {
  const dx = x - n.x;
  const dy = y - n.y;
  const d = Math.abs(dx) + Math.abs(dy);
  if (d < 900) return true;
  return heroWalkable(s, cellAtWorld(s, n.x + Math.trunc((dx * 800) / d), n.y + Math.trunc((dy * 800) / d)));
};

/** A person's blows this second: one from each nightmare close by. A work's blows add up to its `drop`, or stop it. */
const struck = (s: BattleState, r: SendaRules, st: St, n: SendaNpc, def: NpcIn, working: boolean): void => {
  // (The stretcher's blows are its own: battle/litter.ts.)
  if (r.litter) return;
  let got = 0;
  // (What travels under the stone, out of reach, strikes nobody: the counterweight's serpent.)
  // (Nor do the windbreak's crows: they go for the hero, pecking past Kel.)
  // (Nor the watchtower's soot crows: the paladin's second altar.)
  // (Nor the crows the crew scares down the wall at night: the ranger's chapter 2, battle/cliff.ts.)
  const crows = !!r.gust || r.cliff?.act === 'night';
  // (Nor the bridge's mammoths: they go straight for the altar, trampling past the keepers.)
  const tramples = !!r.arch;
  // (Nor La Que Huyó and her squires: the seventh altar counts their blows itself.)
  const own = !!r.seventh;
  for (const e of s.enemies) {
    if (own && sevOwnBlow(s, e)) continue;
    // (Nor the Grey Wolf: the den's fight counts its bites itself.)
    if (r.roca?.wolf && wolfOwnBlow(s, e)) continue;
    // (Nor the workshop's golems: their blow is their own, an arm brought down, battle/golems.ts.)
    if (r.golem && golemOf(s, e.id)) continue;
    // (Nor the cable's flyers: they strike their own blows, battle/garg.ts.)
    if (r.garg && gargOf(s, e.id)) continue;
    // (Nor La Tenaza: its pincers are its own, battle/tenaza.ts.)
    if (r.cams && tenazaOf(s, e.id)) continue;
    // (Nor the mine's beetles, nor La Barrena: their slams and slashes are their own, battle/carts.ts, barrena.ts.)
    if (r.carts && cartBeetle(s, e.id)) continue;
    if (r.drills && barrenaOf(s, e.id)) continue;
    // (Nor Tormo: his fist, stamp and hammer are his own, battle/plates.ts.)
    if (r.plates && platesOwn(s, e.id)) continue;
    // (Nor the Roller: its bite and its roll are its own, battle/fallen.ts.)
    if (r.fallen && fallenOwn(s, e.id)) continue;
    // (Nor the shriekers and their mother: their bites are their own, battle/cliff.ts.)
    if (r.cliff && cliffOwn(s, e.id)) continue;
    if (e.hp <= 0 || e.kind === 'boneWall' || (tramples && e.kind === 'mammoth') || (e.phase === 1 && NIGHTMARES[e.kind].tunnels) || (crows && NIGHTMARES[e.kind].huntsHeroes) || (r.kindle && e.kind === 'crow') || (r.summer && e.kind === 'crow') || (r.autumn && autumnSpares(s, e)) || (r.winter && winterSpares(s, e)) || (r.deep && deepSpares(s, e))) continue;
    // (The druid's clearing: a slime that ate lands heavier blows, druid.ts `gladeBlow`.)
    if (withinRange(e.x, e.y, n.x, n.y, r.hit.range)) got += r.druid?.glade ? gladeBlow(s, e) : 1;
  }
  // (The bridge's keepers under the paladin's aura: part of their blows only.)
  if (r.arch) got = archCover(s, n, got);
  if (!got) return;
  n.hp -= got;
  ev(s, { t: 'senda', kind: 'hit', npc: n.id, x: n.x, y: n.y });
  if (def.drop) {
    // Blows while working add up; at the rule's count the work starts over (no pause: «Sigo. Sigo.»).
    if (working && n.held > 0) {
      n.blows = (n.blows ?? 0) + got;
      if (n.blows >= def.drop) {
        n.held = 0;
        n.blows = 0;
        ev(s, { t: 'senda', kind: 'drop', npc: n.id, x: n.x, y: n.y, n: n.spot });
      }
    }
  } else {
    n.flinch = r.hit.flinch;
    n.held = 0;
  }
  if (!n.hit) {
    n.hit = true;
    say(s, r, st, 'npcHit', n.id);
  }
  if (n.hp <= 0) {
    n.hp = 0;
    ev(s, { t: 'senda', kind: 'down', npc: n.id, x: n.x, y: n.y });
  }
};

export const sendaTick = (s: BattleState): void => {
  const r = s.rules?.senda;
  if (!r) return;
  if (!s.senda) {
    s.senda = fresh(r);
    // Who follows starts by the hero when its own spot isn't floor (a descent's rooms are wherever the map put them).
    const h0 = s.heroes[0];
    s.senda.npcs.forEach((n, i) => {
      if (h0 && (r.npcs[i] as NpcIn).does === 'follow' && !heroWalkable(s, cellAtWorld(s, n.x, n.y))) {
        n.x = h0.x + 300;
        n.y = h0.y + 300;
      }
    });
    // Its own ground puts the hero where the act begins (by Kel).
    if (h0 && r.start) {
      h0.x = h0.destX = h0.ax = r.start.x;
      h0.y = h0.destY = h0.ay = r.start.y;
      h0.path = [];
      h0.moving = false;
    }
  }
  const st = s.senda;
  const cue = (c: string): void => sendaCue(s, c);
  if (r.wedge) wedgeTick(s, cue);
  if (r.hooks) hooksTick(s, cue);
  if (r.gust) gustTick(s, cue);
  if (r.weight) weightTick(s, cue);
  if (r.slope) slopeTick(s, cue);
  if (r.roof) roofTick(s, cue);
  if (r.slide) slideTick(s, cue);
  if (r.cave) caveTick(s, cue);
  if (r.crag) cragTick(s, cue);
  if (r.line) lineTick(s, cue);
  if (r.beam) beamTick(s, cue);
  if (r.storm) stormTick(s, cue);
  if (r.canal) canalTick(s, cue);
  if (r.kindle) kindleTick(s, cue);
  if (r.salt) saltTick(s, cue);
  if (r.arch) archTick(s, cue);
  if (r.monastery) monasteryTick(s, cue);
  if (r.seventh) seventhTick(s, cue);
  if (r.cornice) corniceTick(s, cue);
  if (r.workshop) workshopTick(s, cue);
  if (r.gears) gearsTick(s, cue);
  if (r.crane) craneTick(s, cue);
  if (r.gondola) gondolaTick(s, cue);
  if (r.gearbox) gearboxTick(s, cue);
  if (r.vent) ventTick(s, cue);
  if (r.ember) emberTick(s, cue);
  if (r.cams) camsTick(s, cue);
  if (r.struts) strutsTick(s, cue);
  if (r.carts) cartsTick(s, cue);
  if (r.drills) drillsTick(s, cue);
  if (r.knees) kneesTick(s, cue);
  if (r.torno) tornoTick(s, cue);
  if (r.shell) shellTick(s, cue);
  if (r.plates) platesTick(s, cue);
  if (r.pens) pensTick(s, cue);
  if (r.trail) trailTick(s, cue);
  if (r.vert) vertTick(s, cue);
  if (r.bote) boteTick(s, cue);
  if (r.isla) islaTick(s, cue);
  if (r.roca) rocaTick(s, cue);
  if (r.litter) litterTick(s, cue);
  if (r.druid) druidTick(s, cue);
  if (r.summer) summerTick(s, cue);
  if (r.autumn) autumnTick(s, cue);
  if (r.winter) winterTick(s, cue);
  if (r.deep) deepTick(s, cue);
  if (r.cairns) cairnsTick(s, cue);
  if (r.echo) echoTick(s, cue);
  if (r.fallen) fallenTick(s, cue);
  if (r.cliff) cliffTick(s, cue);
  if (r.soot) sootTick(s, cue);
  if (r.seals) sealsTick(s, cue);
  if (!s.started) return;
  // Every tick, but each line once (an empty field fast-forwards the wave's clock past its first tick).
  say(s, r, st, 'start');
  say(s, r, st, 'at');
  if (st.queued?.length) {
    const due = st.queued.filter((q) => q.at <= s.tick);
    if (due.length) {
      st.queued = st.queued.filter((q) => q.at > s.tick);
      for (const q of due) speak(s, st, r.says[q.i] as Say);
    }
  }
  if (!st.boss && bossOf(s)) {
    st.boss = true;
    say(s, r, st, 'bossIn');
  }
  if (!st.half) {
    const g = sendaGoal(s);
    if (g.done * 2 >= g.need && (r.goal.kind !== 'boss' || st.boss)) {
      st.half = true;
      say(s, r, st, 'half');
    }
  }
  const hero = s.heroes.find((h) => h.downTicks === 0);
  const blow = s.tick % r.hit.every === 0;
  for (let i = 0; i < st.npcs.length; i++) {
    const n = st.npcs[i] as SendaNpc;
    const def = r.npcs[i] as NpcIn;
    if (n.hp <= 0 || n.away) continue;
    // (Sela on a rope after the hero: the wall's trip moves her, battle/cliff.ts.)
    if (r.cliff && cliffHolds(s, n)) continue;
    const spot = def.spots[n.spot];
    const working = def.does === 'work' && !!spot && n.x === spot.x && n.y === spot.y;
    if (blow && !def.safe) {
      struck(s, r, st, n, def, working);
      if (n.hp <= 0) continue;
    }
    // (Ceda walks her own way under the leaves: the druid's chapter 3.)
    if (!(r.autumn && autumnWalks(s, n))) n.moving = false;
    if (def.does === 'follow') {
      if (hero && !withinRange(hero.x, hero.y, n.x, n.y, def.follow ?? r.follow)) {
        n.moving = true;
        walk(s, n, hero.x, hero.y, r.speed);
      }
    } else if (def.does === 'work') {
      if (!spot) continue;
      if (!working) {
        n.moving = true;
        walk(s, n, spot.x, spot.y, r.speed);
        n.held = 0;
        n.blows = 0;
      } else if (n.flinch > 0) n.flinch--;
      else if (def.near && !(hero && withinRange(hero.x, hero.y, n.x, n.y, def.near))) continue;
      else {
        if (n.held === 0) sendaCue(s, `dig${n.spot + 1}`);
        if (++n.held >= def.ticks) {
          n.held = 0;
          n.blows = 0;
          n.spot++;
          st.work++;
          ev(s, { t: 'senda', kind: 'work', npc: n.id, x: n.x, y: n.y, n: st.work });
          sendaCue(s, `done${st.work}`);
        }
      }
    }
  }
};

/**
 * An open clearing's hunt (`rules.senda.hunt`): a nightmare goes for the nearest of the standing hero and the path's
 * people who can be struck. Going for a person, it walks up and stands at them (their blows come from `struck`, once a
 * second); for the hero (or taunted), false: the usual chase.
 */
export const sendaHunt = (s: BattleState, e: Enemy, px: number): boolean => {
  const r = s.rules?.senda;
  const st = s.senda;
  if (!r?.hunt || !st || e.taunter > 0) return false;
  // The Burn's crías on the glass shore hunt the hero while it is dry (they hunt heroes); in the water they leave it be
  // and go for whoever is near (Ser Marit): «Póngase en el agua» has its price.
  if (r.canal && e.kind === 'wyrmling') {
    const h = s.heroes[0];
    if (h && h.downTicks === 0 && !canalWet(s, cellAtWorld(s, h.x, h.y))) return false;
  }
  let bd = 0x7fffffff;
  for (const h of s.heroes) {
    if (h.downTicks > 0 || h.light !== undefined) continue;
    const d = dist2(e.x, e.y, h.x, h.y);
    if (d < bd) bd = d;
  }
  let best: SendaNpc | null = null;
  // Crows on the windbreak go for the hero (they hunt heroes: the people they leave be).
  // (And the soot crows at the watchtower: they peck at the hero, not at Marit or the altar.)
  // (And the Slope of the kilns' soot crows: the druid's chapter 2.)
  // (And the leeches of the rot in the druid's memory: they cling to Olmo.)
  const heroesOnly = (!!r.gust && !!NIGHTMARES[e.kind].huntsHeroes) || (!!r.kindle && e.kind === 'crow') || (!!r.summer && e.kind === 'crow') || (!!r.autumn && e.kind === 'leech') || (!!r.winter && e.kind === 'crow');
  for (let i = 0; i < st.npcs.length; i++) {
    const n = st.npcs[i] as SendaNpc;
    if (heroesOnly || n.hp <= 0 || n.away || (r.npcs[i] as NpcIn).safe) continue;
    const d = dist2(e.x, e.y, n.x, n.y);
    if (d < bd) {
      bd = d;
      best = n;
    }
  }
  if (!best) return false;
  e.tgtKind = 0;
  e.tgtId = 0;
  if (withinRange(e.x, e.y, best.x, best.y, NIGHTMARES[e.kind as keyof typeof NIGHTMARES].reach + 150)) {
    if (best.x !== e.x || best.y !== e.y) e.facing = iatan2(best.y - e.y, best.x - e.x);
    if (e.cd === 0) {
      e.cd = e.reload;
      e.anim = 'attack';
      e.animT = 0;
    }
    return true;
  }
  e.anim = 'walk';
  stepToward(e, best.x, best.y, px);
  return true;
};

/**
 * Lost with the hero down or a vital person down; won with the goal reached. Null leaves it to the ground's own rule
 * (the node held or fallen, the boss), unless the fight has its own ground.
 */
export const sendaResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda;
  const st = s.senda;
  if (!r || !st) return null;
  if (s.heroes.length && s.heroes.every((h) => h.downTicks > 0)) return 'lost';
  for (let i = 0; i < st.npcs.length; i++) if ((st.npcs[i] as SendaNpc).hp <= 0 && (r.npcs[i] as NpcIn).vital) return 'lost';
  const g = r.goal;
  if (g.kind === 'wedge') return wedgeResult(s);
  if (g.kind === 'winch') return hooksResult(s);
  if (g.kind === 'cross') return gustResult(s);
  if (g.kind === 'weight') return weightResult(s);
  if (g.kind === 'door') return slopeResult(s);
  if (g.kind === 'mouth' || g.kind === 'avalanche') return slideResult(s);
  if (g.kind === 'hall') return caveResult(s);
  if (g.kind === 'crag') return cragResult(s);
  if (g.kind === 'line') return lineResult(s);
  if (g.kind === 'beam') return beamResult(s);
  if (g.kind === 'storm') return stormResult(s);
  if (g.kind === 'pool' || g.kind === 'ebb') return canalResult(s);
  if (g.kind === 'house' || g.kind === 'drain') return saltResult(s);
  if (g.kind === 'ends' || g.kind === 'cistern' || g.kind === 'mammoth') return archResult(s);
  if (g.kind === 'tide' || g.kind === 'cell' || g.kind === 'sixth') return monasteryResult(s);
  if (g.kind === 'rubble' || g.kind === 'ring' || g.kind === 'fled') return seventhResult(s);
  if (g.kind === 'grillo') return corniceResult(s);
  if (g.kind === 'tormo') return workshopResult(s);
  if (g.kind === 'lift') return gearsResult(s);
  if (g.kind === 'crane') return craneResult(s);
  if (g.kind === 'span') return gondolaResult(s);
  if (g.kind === 'haul') return gearboxResult(s);
  if (g.kind === 'marrow') return ventResult(s);
  if (g.kind === 'unjam') return emberResult(s);
  if (g.kind === 'stone') return camsResult(s);
  if (g.kind === 'struts') return strutsResult(s);
  if (g.kind === 'drillHall') return cartsResult(s);
  if (g.kind === 'geode') return drillsResult(s);
  if (g.kind === 'knees') return kneesResult(s);
  if (g.kind === 'raja') return tornoResult(s);
  if (g.kind === 'shell') return shellResult(s);
  if (g.kind === 'free') return platesResult(s);
  if (g.kind === 'herd') return pensResult(s);
  if (g.kind === 'trail') return trailResult(s);
  if (g.kind === 'way' || g.kind === 'leaps' || g.kind === 'boat') return vertResult(s);
  if (g.kind === 'wake' || g.kind === 'waves' || g.kind === 'elder') return boteResult(s);
  if (g.kind === 'floes' || g.kind === 'lagoon' || g.kind === 'seal') return islaResult(s);
  if (g.kind === 'climb' || g.kind === 'mouths' || g.kind === 'bed' || g.kind === 'wolf') return rocaResult(s);
  if (g.kind === 'litter' || g.kind === 'lamp' || g.kind === 'night' || g.kind === 'dawn' || g.kind === 'mirrors' || g.kind === 'lamps' || g.kind === 'procession' || g.kind === 'mourn') return litterResult(s);
  if (g.kind === 'glade' || g.kind === 'face' || g.kind === 'bridge') return druidResult(s);
  if (g.kind === 'kilns' || g.kind === 'strip' || g.kind === 'cone') return summerResult(s);
  if (g.kind === 'maple' || g.kind === 'houses' || g.kind === 'rot') return autumnResult(s);
  if (g.kind === 'crown' || g.kind === 'bandage' || g.kind === 'sap') return winterResult(s);
  if (g.kind === 'deepMouths' || g.kind === 'carcoma') return deepResult(s);
  if (g.kind === 'net') return autumnResult(s);
  if (g.kind === 'cairns') return cairnsResult(s);
  if (g.kind === 'echo') return echoResult(s);
  if (g.kind === 'fallen') return fallenResult(s);
  if (g.kind === 'cliffEdge' || g.kind === 'cliffWall' || g.kind === 'cliffNight') return cliffResult(s);
  if (g.kind === 'catalog') return sootResult(s);
  if (g.kind === 'locks' || g.kind === 'cabinet') return sealsResult(s);
  if (g.kind === 'reach') return s.dungeon?.reached ? 'won' : null;
  if (r.roof) {
    const out = roofResult(s);
    if (out || g.kind === 'troll') return out;
  }
  if (g.kind === 'boss') {
    const id = bossOf(s);
    return st.boss && id && !s.enemies.some((e) => e.id === id && e.hp > 0) ? 'won' : null;
  }
  if (g.kind === 'clear') return s.wave.next >= s.wave.spawns.length && !s.enemies.some((e) => e.hp > 0 && e.kind !== 'boneWall') ? 'won' : null;
  const p = sendaGoal(s);
  return p.done >= p.need ? 'won' : null;
};
